import { useCallback, useMemo } from 'react';
import {
  BRANCHES_KEY,
  branchName,
  departmentsOfBranch,
  parseBranches,
  type Branch,
} from '@/domain/branches';
import { buildDirectory, type Directory } from '@/domain/directory';
import { staffInScope } from '@/domain/people';
import { passesViewFilter, type ViewFilterContext } from '@/domain/view-filter';
import { useCurrentUser, useWorkspace } from '@/stores/session-store';
import { useViewFilterStore } from '@/stores/view-filter-store';

export function useDirectory(): Directory {
  const { users, orgUnits } = useWorkspace();
  return useMemo(() => buildDirectory(users, orgUnits), [users, orgUnits]);
}

/**
 * Chi nhánh Giám đốc đã khai ở Cài đặt. Đọc THẲNG từ store chứ không qua `use-records`: file đó đã
 * nhập `useOwnerFilter` của chính file này, đi vòng lại là vòng nhập lẫn nhau.
 */
export function useBranches(): Branch[] {
  const record = useWorkspace().records.setting?.[0];
  return useMemo(() => parseBranches(record?.data?.[BRANCHES_KEY]), [record]);
}

/** Tra tên cho câu «đang lọc: …» — danh bạ cộng thêm tên chi nhánh. */
export function useViewFilterNames() {
  const directory = useDirectory();
  const branches = useBranches();
  return useMemo(
    () => ({
      displayName: directory.displayName,
      unitName: directory.unitName,
      branchName: (id: string) => branchName(branches, id),
    }),
    [directory, branches],
  );
}

/** Hàm «bản ghi của người này có đang được chọn xem không» theo bộ lọc xem hiện tại. */
export function useOwnerFilter(): (owner: string | null) => boolean {
  const filter = useViewFilterStore((state) => state.filter);
  const { userByUsername } = useDirectory();
  const branches = useBranches();
  const context: ViewFilterContext = useMemo(
    () => ({ userByUsername, departmentsOfBranch: (id) => departmentsOfBranch(branches, id) }),
    [userByUsername, branches],
  );
  return useCallback((owner) => passesViewFilter(filter, owner, context), [filter, context]);
}

/**
 * Những người đang được XEM trên các bảng theo người (KPI, đánh giá, báo cáo công việc): nhân sự đang làm
 * việc trong phạm vi, trừ Giám đốc (`staffInScope`), rồi lọc theo thanh «Xem: phòng · nhóm · người».
 */
export function useStaffInView(): string[] {
  const { users } = useWorkspace();
  const { visibleUsernames } = useCurrentUser();
  const passes = useOwnerFilter();
  return useMemo(
    () => staffInScope(users, visibleUsernames).filter((username) => passes(username)),
    [users, visibleUsernames, passes],
  );
}
